import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { saveOwnSignature, getOwnSignatureImage, deleteOwnSignature } from "../../../services/user-service";
import { SET_USER_SIGNATURE } from "../../../redux/action/action-types";
import { DS } from "../../../theme-default";
import "./signature-settings.css";

const WIDTH = 1000;
const HEIGHT = 360;

export function SignatureSettings({ signatureLink, onSaved }) {
  const canvas = useRef(null);
  const strokes = useRef([]);
  const activePointer = useRef(null);
  const baseImage = useRef(null);
  const editingLink = useRef(null);
  const [strokeCount, setStrokeCount] = useState(0);
  const [drawing, setDrawing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [hasBaseImage, setHasBaseImage] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const existingLink = (signatureLink || "").trim();
  const hasDrawing = hasBaseImage || strokeCount > 0;
  const busy = saving || loadingImage || deleting;

  useEffect(() => {
    if (canvas.current) redraw();
  }, [editing, existingLink]);

  function redraw() {
    const context = canvas.current.getContext("2d");
    context.clearRect(0, 0, WIDTH, HEIGHT);
    if (baseImage.current) context.drawImage(baseImage.current, 0, 0);
    context.strokeStyle = DS.primary;
    context.fillStyle = DS.primary;
    context.lineWidth = 4.5;
    context.lineCap = "round";
    context.lineJoin = "round";
    strokes.current.forEach(points => {
      context.beginPath();
      if (points.length === 1) {
        context.arc(points[0].x, points[0].y, 2.25, 0, Math.PI * 2);
        context.fill();
      } else {
        context.moveTo(points[0].x, points[0].y);
        points.slice(1).forEach(point => context.lineTo(point.x, point.y));
        context.stroke();
      }
    });
  }

  function point(event) {
    const rect = canvas.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(WIDTH, (event.clientX - rect.left) * WIDTH / rect.width)),
      y: Math.max(0, Math.min(HEIGHT, (event.clientY - rect.top) * HEIGHT / rect.height))
    };
  }

  function startStroke(event) {
    if (!event.isPrimary || event.button !== 0 || saving || preview) return;
    event.preventDefault();
    activePointer.current = event.pointerId;
    canvas.current.setPointerCapture(event.pointerId);
    strokes.current.push([point(event)]);
    setStrokeCount(strokes.current.length);
    setDrawing(true);
    setError("");
    redraw();
  }

  function moveStroke(event) {
    if (event.pointerId !== activePointer.current) return;
    event.preventDefault();
    const samples = event.nativeEvent.getCoalescedEvents
      ? event.nativeEvent.getCoalescedEvents() : [event];
    (samples.length ? samples : [event]).forEach(sample => {
      strokes.current[strokes.current.length - 1].push(point(sample));
    });
    redraw();
  }

  function endStroke(event) {
    if (event.pointerId !== activePointer.current) return;
    activePointer.current = null;
    setDrawing(false);
    if (canvas.current.hasPointerCapture(event.pointerId)) {
      canvas.current.releasePointerCapture(event.pointerId);
    }
  }

  function removeStrokes(all) {
    if (all) {
      baseImage.current = null;
      setHasBaseImage(false);
    }
    strokes.current = all ? [] : strokes.current.slice(0, -1);
    setStrokeCount(strokes.current.length);
    setError("");
    redraw();
  }

  function resetDrawing() {
    baseImage.current = null;
    strokes.current = [];
    setHasBaseImage(false);
    setStrokeCount(0);
    setPreview(null);
    setError("");
    setMessage("");
    setConfirmDelete(false);
  }

  function startBlank() {
    resetDrawing();
    editingLink.current = existingLink;
    setEditing(true);
  }

  async function editSaved() {
    setLoadingImage(true);
    setError("");
    try {
      const response = await getOwnSignatureImage();
      const url = URL.createObjectURL(response.data);
      const image = new Image();
      try {
        await new Promise((resolve, reject) => {
          image.onload = resolve;
          image.onerror = reject;
          image.src = url;
        });
      } finally {
        URL.revokeObjectURL(url);
      }
      const imported = document.createElement("canvas");
      imported.width = WIDTH;
      imported.height = HEIGHT;
      const context = imported.getContext("2d");
      const scale = Math.min((WIDTH - 48) / image.width, (HEIGHT - 48) / image.height);
      const width = image.width * scale, height = image.height * scale;
      context.drawImage(image, (WIDTH - width) / 2, (HEIGHT - height) / 2, width, height);
      // Legacy uploads may have white paper behind the ink. Keep the editor transparent and blue.
      const pixels = context.getImageData(0, 0, WIDTH, HEIGHT);
      const blue = parseInt(DS.primary.slice(1), 16);
      for (let i = 0; i < pixels.data.length; i += 4) {
        if (pixels.data[i] >= 235 && pixels.data[i + 1] >= 235 && pixels.data[i + 2] >= 235) {
          pixels.data[i + 3] = 0;
        } else if (pixels.data[i + 3]) {
          pixels.data[i] = blue >> 16 & 255;
          pixels.data[i + 1] = blue >> 8 & 255;
          pixels.data[i + 2] = blue & 255;
        }
      }
      context.putImageData(pixels, 0, 0);
      resetDrawing();
      baseImage.current = imported;
      editingLink.current = existingLink;
      setHasBaseImage(true);
      setEditing(true);
    } catch (failure) {
      setError("Semnătura nu a putut fi încărcată pentru editare. Încercați din nou sau alegeți Redesenează.");
    } finally {
      setLoadingImage(false);
    }
  }

  async function deleteSaved() {
    if (deleting) return;
    setDeleting(true);
    setError("");
    try {
      await deleteOwnSignature(existingLink);
      resetDrawing();
      editingLink.current = null;
      setEditing(false);
      onSaved(null);
      setMessage("Semnătura a fost ștearsă. Puteți desena o semnătură nouă.");
    } catch (failure) {
      setError(failure.response && failure.response.status === 409
        ? "Semnătura a fost modificată între timp. Reîncărcați pagina înainte de a continua."
        : "Semnătura nu a putut fi ștearsă. Încercați din nou.");
    } finally {
      setDeleting(false);
    }
  }

  async function save() {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      // Canvas pixels are transparent; the visible paper and guide are CSS only.
      const blob = await new Promise(resolve => canvas.current.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("PNG export failed");
      const result = await saveOwnSignature(blob, editingLink.current);
      onSaved(result.data);
      setEditing(false);
      setPreview(null);
      setMessage(editingLink.current ? "Semnătura a fost actualizată." : "Semnătura a fost salvată.");
    } catch (failure) {
      const response = failure.response;
      if (response && response.status === 409) {
        setError("Semnătura a fost modificată între timp. Reîncărcați pagina înainte de a continua.");
      } else if (response && response.status === 400) {
        setError("Desenați o semnătură completă înainte de salvare.");
        setPreview(null);
      } else {
        setError("Semnătura nu a putut fi salvată. Desenul a fost păstrat; încercați din nou.");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="signature-settings ds-card" id="signature" aria-labelledby="signature-heading" aria-busy={busy}>
      <h3 id="signature-heading">Semnătura mea</h3>
      {message && <p className="signature-success" role="status">{message}</p>}
      {existingLink && !editing ? (
        <div>
          <p>Semnătura salvată este folosită pe referatele de necesitate. Modificarea sau ștergerea ei nu afectează referatele deja aprobate.</p>
          <div className="signature-preview">
            <img src={existingLink} alt="Semnătura dumneavoastră" />
          </div>
          {confirmDelete ? (
            <div className="signature-delete-confirmation">
              <p>Ștergeți semnătura salvată din cont?</p>
              <div className="signature-actions">
                <button type="button" disabled={busy} onClick={() => setConfirmDelete(false)}>Renunță</button>
                <button type="button" className="signature-danger" disabled={busy} onClick={deleteSaved}>
                  {deleting ? "Se șterge…" : "Confirmă ștergerea"}
                </button>
              </div>
            </div>
          ) : (
            <div className="signature-actions">
              <button type="button" disabled={busy} onClick={editSaved}>{loadingImage ? "Se încarcă…" : "Editează"}</button>
              <button type="button" disabled={busy} onClick={startBlank}>Redesenează</button>
              <button type="button" className="signature-danger" disabled={busy} onClick={() => { setError(""); setConfirmDelete(true); }}>Șterge semnătura</button>
            </div>
          )}
        </div>
      ) : (
        <div>
          <h4>{editing ? "Modificați semnătura dumneavoastră" : "Desenați semnătura dumneavoastră"}</h4>
          <p id="signature-instructions">
            Folosiți mouse-ul, degetul sau un creion digital. Semnătura va fi salvată
            cu cerneală albastră, fără fundal, pentru a fi folosită pe referate.
          </p>
          <div className="signature-drawing" style={{ display: preview ? "none" : undefined }}>
            <canvas
              ref={canvas}
              width={WIDTH}
              height={HEIGHT}
              aria-label="Zona pentru desenarea semnăturii"
              aria-describedby="signature-instructions"
              onPointerDown={startStroke}
              onPointerMove={moveStroke}
              onPointerUp={endStroke}
              onPointerCancel={endStroke}
              onLostPointerCapture={endStroke}
            >
              Browserul dumneavoastră nu permite desenarea semnăturii.
            </canvas>
            {!hasDrawing && <span className="signature-placeholder">Semnați aici</span>}
          </div>
          {preview && (
            <div className="signature-preview">
              <img src={preview} alt="Previzualizarea semnăturii desenate" />
            </div>
          )}
          <p className="signature-hint">
            {preview
              ? "Verificați semnătura înainte de salvare. O puteți modifica oricând din setările contului."
              : "Puteți șterge și redesena semnătura înainte de a o salva."}
          </p>
          <div className="signature-actions">
            {preview ? (
              <React.Fragment>
                <button type="button" disabled={saving} onClick={() => setPreview(null)}>
                  Continuă desenul
                </button>
                <button type="button" className="signature-primary" disabled={saving} onClick={save}>
                  {saving ? "Se salvează…" : editing ? "Salvează modificările" : "Salvează semnătura"}
                </button>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <button type="button" disabled={!strokeCount || drawing} onClick={() => removeStrokes(false)}>
                  Anulează ultima linie
                </button>
                <button type="button" disabled={!hasDrawing || drawing} onClick={() => removeStrokes(true)}>
                  Șterge tot
                </button>
                <button type="button" className="signature-primary" disabled={!hasDrawing || drawing}
                  onClick={() => setPreview(canvas.current.toDataURL("image/png"))}>
                  Previzualizează
                </button>
              </React.Fragment>
            )}
            {editing && <button type="button" disabled={saving || drawing} onClick={() => { resetDrawing(); setEditing(false); }}>Renunță la modificări</button>}
          </div>
        </div>
      )}
      {error && <p className="signature-error" role="alert">{error}</p>}
    </section>
  );
}

export default connect(
  state => ({ signatureLink: state.login.user.signatureLink }),
  dispatch => ({
    onSaved: signatureLink => dispatch({ type: SET_USER_SIGNATURE, payload: signatureLink })
  })
)(SignatureSettings);
